import AgentTogglesSection from '@/components/agents/AgentTogglesSection';
import PreferenceBlocks from '@/components/companion/PreferenceBlocks';
import { PageHeader } from '@/components/ui';
import { useAuth } from '@/contexts/auth-context';
import { useSavePreferencesAction } from '@/hooks/actions/use-save-preferences-action';
import { useBrandDnaQuery } from '@/hooks/queries/use-brand-dna-query';
import { useDiscoveryPreferencesQuery } from '@/hooks/queries/use-discovery-preferences-query';
import { usePageTitleStore } from '@/stores/page-title';
import { Box, Button, Flex, Separator, Spinner, Text, VStack } from '@chakra-ui/react';
import { type BrandDiscoveryPreferences } from '@fluenceur/types';
import { parseAsBoolean, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import BrandDnaModal from './components/BrandDnaModal';
import SettingsSection from './components/SettingsSection';

const AgentsSettingsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const brandId = user?.currentBrand?.id ?? '';

  const { data: dnaData, isLoading: isDnaLoading } = useBrandDnaQuery();
  const { data: prefs } = useDiscoveryPreferencesQuery(brandId);
  const savePreferences = useSavePreferencesAction(brandId);

  const setTitle = usePageTitleStore((state) => state.setTitle);

  const [prefsDraft, setPrefsDraft] = useState<BrandDiscoveryPreferences | null>(null);
  const [prefsDirty, setPrefsDirty] = useState(false);
  // URL-backed so the modal survives refresh and deep links keep working
  // (the former /settings/brand-dna page used the same `setup` param).
  const [isDnaModalOpen, setIsDnaModalOpen] = useQueryState(
    'setup',
    parseAsBoolean.withDefault(false)
  );

  useEffect(() => {
    setTitle(t('settings.agents.title'));
    return () => setTitle('');
  }, [setTitle, t]);

  // Seed draft from server data once it arrives. Never re-seed if the user
  // has uncommitted edits (prefsDirty guard).
  useEffect(() => {
    if (prefs && !prefsDirty) {
      setPrefsDraft(prefs);
    }
  }, [prefs, prefsDirty]);

  const handlePatch = (changes: Partial<BrandDiscoveryPreferences>) => {
    setPrefsDraft((current) => {
      const base: BrandDiscoveryPreferences = current ?? {
        sizeTargets: [],
        nicheTags: [],
        tone: [],
        antiNiche: [],
        geo: [],
      };
      return { ...base, ...changes };
    });
    setPrefsDirty(true);
  };

  const handleSavePrefs = () => {
    if (!prefsDraft) return;
    savePreferences.mutate(prefsDraft, {
      onSuccess: () => setPrefsDirty(false),
    });
  };

  // Same guard as the wizard: all filtering axes empty = the scorer skips
  // the brand silently (A3) — never let Settings save that state.
  const hasFilteringCriteria =
    !!prefsDraft &&
    prefsDraft.sizeTargets.length +
      prefsDraft.nicheTags.length +
      prefsDraft.antiNiche.length +
      prefsDraft.geo.length >
      0;

  const hasDna = !!dnaData?.brandDna;
  const hasPrefs = !!prefs;

  return (
    <Box maxW="1000px" w="full">
      <PageHeader title={t('settings.agents.title')} titleSize="xl" marginBottom={4} />

      {/* Brand DNA summary */}
      <SettingsSection
        title={t('settings.companion.dna.title')}
        description={t('settings.companion.dna.description')}
        actions={
          <Button variant="outline" size="sm" onClick={() => setIsDnaModalOpen(true)}>
            {hasDna ? t('settings.companion.dna.edit') : t('settings.companion.dna.setup')}
          </Button>
        }
      >
        {isDnaLoading ? (
          <Spinner size="sm" />
        ) : hasDna ? (
          <Text fontSize="sm" color="fg" lineClamp={2} whiteSpace="pre-wrap">
            {dnaData.brandDna}
          </Text>
        ) : (
          <Text fontSize="sm" color="fg.muted">
            {t('settings.companion.dna.empty')}
          </Text>
        )}
      </SettingsSection>

      <Separator />

      {/* Matching preferences */}
      <SettingsSection
        title={t('settings.companion.prefs.title')}
        description={t('settings.companion.prefs.description')}
        layout="stacked"
      >
        {!hasPrefs && !prefsDraft ? (
          <VStack align="start" gap={3}>
            <Text fontSize="sm" color="fg.muted">
              {t('settings.companion.prefsEmpty')}
            </Text>
            <Button variant="outline" size="sm" asChild>
              <RouterLink to="/onboarding/companion">
                {t('settings.companion.prefsEmptyCta')}
              </RouterLink>
            </Button>
          </VStack>
        ) : (
          <VStack align="stretch" gap={6}>
            <PreferenceBlocks draft={prefsDraft} onPatch={handlePatch} />
            <Flex justify="flex-end">
              <Button
                colorPalette="primary"
                size="sm"
                onClick={handleSavePrefs}
                disabled={!prefsDraft || !hasFilteringCriteria || savePreferences.isPending}
                loading={savePreferences.isPending}
              >
                {t('settings.companion.savePrefs')}
              </Button>
            </Flex>
          </VStack>
        )}
      </SettingsSection>

      <Separator />

      {/* Agent toggles */}
      <SettingsSection
        title={t('settings.agents.toggles.title')}
        description={t('settings.agents.toggles.description')}
        isLast
      >
        <AgentTogglesSection brandId={brandId} />
      </SettingsSection>

      <BrandDnaModal
        isOpen={isDnaModalOpen}
        onClose={() => setIsDnaModalOpen(null)}
        existingDna={dnaData?.brandDna}
        existingSourceUrl={dnaData?.brandDnaSourceUrl}
      />
    </Box>
  );
};

export default AgentsSettingsPage;
